/**
 * Sign-in backends behind ``AuthProvider``. The server decides which one
 * runs (``GET /api/v1/auth/config`` follows ``OMNI_AUTH_DRIVER``), so
 * switching auth is a backend setting, never a dashboard rebuild.
 *
 * Every backend ends in the same place: a bearer JWT the API verifies.
 * The UI reads its claims (``decodeClaims``) and never trusts them — the
 * backend re-verifies on every request.
 */
import { createClient, createConfig } from "@hey-api/client-fetch";
import { getAuthConfig, login, type AuthConfig } from "@/api";
import { API_BASE } from "@/api/baseUrl";
import { clearToken, isExpired, loadToken, saveToken } from "./token";

export type { AuthConfig };
export type AuthDriver = AuthConfig["driver"];

/** Before sign-in there is no session to attach: a plain client. */
const publicClient = createClient(createConfig({ baseUrl: API_BASE }));

export interface AuthBackend {
  /** Token for an existing session (page reload), or null. */
  restore(): Promise<string | null>;
  signIn(email: string, password: string): Promise<string>;
  /** Present only where users may register themselves. */
  signUp?: (email: string, password: string, name: string) => Promise<string>;
  signOut(): Promise<void>;
  /** A currently valid token, refreshed if the backend can; null when signed out. */
  token(): Promise<string | null>;
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const { data, response } = await getAuthConfig({ client: publicClient });
  if (!data) throw new Error(`Could not load sign-in settings (${response.status}).`);
  return data;
}

/** Argon2id + HS256 JWT issued by the backend's ``POST /api/v1/auth/login``. */
export function localUsersBackend(): AuthBackend {
  return {
    async restore() {
      const stored = loadToken();
      if (stored && !isExpired(stored)) return stored;
      if (stored) clearToken();
      return null;
    },
    async signIn(email, password) {
      const { data, response } = await login({ client: publicClient, body: { email, password } });
      if (!data) {
        throw new Error(
          response.status === 401 ? "Wrong email or password." : `Sign-in failed (${response.status}).`,
        );
      }
      const { token } = data;
      saveToken(token);
      return token;
    },
    async signOut() {
      clearToken();
    },
    async token() {
      // No refresh: the session lasts the token's lifetime, then sign in again.
      const stored = loadToken();
      return stored && !isExpired(stored) ? stored : null;
    },
  };
}

/**
 * Neon Auth (managed Better Auth), over its REST API. The session lives in
 * Neon Auth's own cookie (``credentials: "include"``); the API gets a
 * short-lived (15 min) EdDSA JWT from ``/token``, cached until a minute
 * before expiry so each API call does not cost a round trip to Neon.
 *
 * Why not ``@neondatabase/auth``: it is a 0.x beta that pulls ~190
 * transitive packages (server adapters, UI kits, captcha libraries) for
 * the five calls below, and its client answers ``/token`` from a session
 * cache — after a page reload that returned no JWT, signing the user out
 * on every refresh.
 */
export async function neonBackend(url: string): Promise<AuthBackend> {
  const base = url.replace(/\/$/, "");
  let cached: string | null = null;
  const REFRESH_EARLY_MS = 60_000;

  async function call(path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(`${base}${path}`, {
      ...init,
      credentials: "include",
      headers: init.body ? { "Content-Type": "application/json" } : undefined,
    });
  }

  async function fail(res: Response, fallback: string): Promise<never> {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message || fallback);
  }

  async function fetchToken(): Promise<string | null> {
    if (cached && !isExpired(cached, Date.now() + REFRESH_EARLY_MS)) return cached;
    const res = await call("/token");
    if (res.status === 401) {
      cached = null;
      return null;
    }
    if (!res.ok) await fail(res, `Neon Auth token request failed (${res.status}).`);
    cached = ((await res.json()) as { token?: string }).token ?? null;
    return cached;
  }

  async function startSession(res: Response, fallback: string): Promise<string> {
    if (!res.ok) await fail(res, fallback);
    cached = null;
    const token = await fetchToken();
    if (!token) throw new Error("Neon Auth accepted the request but issued no token.");
    return token;
  }

  return {
    async restore() {
      const res = await call("/get-session");
      if (!res.ok) return null;
      const body = (await res.json().catch(() => null)) as { session?: unknown } | null;
      return body?.session ? fetchToken() : null;
    },
    async signIn(email, password) {
      const res = await call("/sign-in/email", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      return startSession(res, "Wrong email or password.");
    },
    async signUp(email, password, name) {
      const res = await call("/sign-up/email", {
        method: "POST",
        body: JSON.stringify({ email, password, name }),
      });
      return startSession(res, "Could not create the account.");
    },
    async signOut() {
      cached = null;
      await call("/sign-out", { method: "POST", body: "{}" });
    },
    token: fetchToken,
  };
}

/**
 * ``dev`` driver only (local work and the e2e stack): the server accepts any
 * bearer, so the browser mints an unsigned token carrying the claims the UI
 * reads. Never reachable against a real auth driver — the server must say
 * ``driver: dev`` first.
 */
export function devBackend(): AuthBackend {
  const mint = (email: string): string => {
    const b64 = (o: unknown) =>
      btoa(JSON.stringify(o)).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
    const exp = Math.floor(Date.now() / 1000) + 12 * 3600;
    return `${b64({ alg: "none", typ: "JWT" })}.${b64({ sub: "dev-user", email, role: "user", exp })}.dev`;
  };
  return {
    async restore() {
      const stored = loadToken();
      return stored && !isExpired(stored) ? stored : null;
    },
    async signIn(email) {
      const token = mint(email || "dev@omni.local");
      saveToken(token);
      return token;
    },
    async signOut() {
      clearToken();
    },
    async token() {
      const stored = loadToken();
      return stored && !isExpired(stored) ? stored : null;
    },
  };
}

/** The one place a driver becomes a backend. Unknown drivers fail loud. */
export async function buildAuthBackend(config: AuthConfig): Promise<AuthBackend> {
  switch (config.driver) {
    case "neon":
      if (!config.neon_auth_url) throw new Error("Server reports Neon Auth without a URL.");
      return neonBackend(config.neon_auth_url);
    case "local_users":
      return localUsersBackend();
    case "dev":
      return devBackend();
    case "oidc":
      throw new Error("This server uses OIDC sign-in, which the dashboard does not support yet.");
  }
}
